import { forwardRef } from 'preact/compat';
import { useRef, useImperativeHandle } from 'preact/hooks';
import Button from 'components/Button';
import { Stack } from 'components/Stack';
import HelperText from 'components/HelperText';
import { CustomText } from 'components/CustomText';

import { useTranslation } from 'react-i18next';
import { usePTPConfigContext } from 'GlobalConfig.context';
import { UploadItem } from '../UploadItem';
import { useUpload } from '../../hooks/useUpload';
import {
  MAX_NUMBER_DOCUMENTS,
  ACCEPTED_FILES,
  MAX_FILE_SIZE,
} from '../../constants';
import {
  AttachmentRef,
  UploadData,
} from '../../../../../../interfaces/ReturnRequest.interface';
import {
  Heading,
  Instruction,
  InstructionContainer,
  UploadContainer,
} from './UploadSection.styles';

interface UploadSectionProps {
  shipmentPk: number;
  uploadedAttachments: UploadData[];
}

export const UploadSection = forwardRef<AttachmentRef, UploadSectionProps>(
  ({ shipmentPk, uploadedAttachments }, ref): JSX.Element => {
    const { t } = useTranslation();
    const {
      return: { customText },
    } = usePTPConfigContext();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const {
      attachments,
      error,
      isUploading,
      hasError,
      shouldDisableUpload,
      handleUpload,
      handleRemove,
    } = useUpload(shipmentPk, uploadedAttachments);

    const acceptedFileNames = ACCEPTED_FILES.map(
      ({ fileName }) => fileName,
    ).join(', ');
    const acceptedFileTypes = ACCEPTED_FILES.map(
      ({ fileType }) => fileType,
    ).join(',');

    // Exposing methods to the parent component
    useImperativeHandle(
      ref,
      () => {
        return {
          isLoading: () => isUploading,
          hasError: () => hasError,
          getAttachments: () => attachments.filter((item) => item.fileUrl),
        };
      },
      [isUploading, hasError, attachments.length],
    );

    return (
      <Stack direction="column" gap={16}>
        <Stack justify="space-between" align="start" gap={32}>
          <InstructionContainer>
            <Heading>{t('RETURN_UPLOAD_ADDITIONAL_ATTACHMENTS')}</Heading>
            <Instruction>
              {customText.uploadAttachment?.text ? (
                <CustomText text={customText.uploadAttachment.text} />
              ) : (
                t('RETURN_ATTACHMENT_UPLOAD_INSTRUCTION', {
                  maxNoDocuments: MAX_NUMBER_DOCUMENTS,
                  maxFileSize: MAX_FILE_SIZE,
                  acceptedFileNames,
                })
              )}
            </Instruction>
          </InstructionContainer>

          <Button
            onClick={() => fileInputRef.current.click()}
            disabled={shouldDisableUpload}
          >
            {t('UPLOAD_CTA')}
          </Button>
          <input
            // set value="" so we can upload same file after remove
            // https://www.pivotaltracker.com/n/projects/2485694/stories/184411242
            value=""
            type="file"
            ref={fileInputRef}
            accept={acceptedFileTypes}
            onChange={handleUpload}
            multiple
            hidden
          />
        </Stack>

        {!!attachments.length && (
          <UploadContainer>
            {attachments.map((attachment) => {
              return (
                <UploadItem
                  key={attachment.id}
                  attachment={attachment}
                  onRemove={handleRemove}
                />
              );
            })}
          </UploadContainer>
        )}

        {error && <HelperText isError>{error}</HelperText>}
      </Stack>
    );
  },
);
